<template>
  <b-modal
    :visible="visible"
    :title="$t('despacho.detail.redirectModalTitle')"
    :ok-title="$t('despacho.detail.confirmRedirectButton')"
    :cancel-title="$t('common.back')"
    ok-variant="warning"
    :size="dispatchId ? 'md' : 'lg'"
    :ok-disabled="saving || !selectedDispatchId || !targetOccurrenceId"
    @show="reset"
    @change="$emit('update:visible', $event)"
    @ok="confirm"
  >
    <!-- Aberto pela fila: a ocorrência pode ter mais de um despacho ativo,
         então pergunta qual. Pelo detalhe o despacho já vem escolhido. -->
    <template v-if="!dispatchId">
      <label class="rdm-section-label">
        <span class="required">*</span> {{ $t('despacho.redirectModal.chooseDispatch') }}
      </label>

      <div
        v-if="loadingDispatches"
        class="text-center py-3"
      >
        <b-spinner
          small
          variant="primary"
        />
      </div>

      <div
        v-else-if="!dispatches.length"
        class="text-muted small mb-2"
      >
        {{ $t('despacho.redirectModal.noActiveDispatch') }}
      </div>

      <div
        v-else
        class="rdm-dispatches"
      >
        <button
          v-for="d in dispatches"
          :key="d.id"
          type="button"
          class="rdm-dispatch"
          :class="{
            'rdm-dispatch--selected': selectedDispatchId === d.id,
            'rdm-dispatch--disabled': !!blockReason(d),
          }"
          :disabled="!!blockReason(d)"
          @click="selectedDispatchId = d.id"
        >
          <div class="rdm-dispatch__head">
            <feather-icon
              :icon="selectedDispatchId === d.id ? 'CheckCircleIcon' : 'CircleIcon'"
              size="14"
              class="mr-50 rdm-dispatch__check"
            />
            <strong>{{ d.team ? d.team.name : '—' }}</strong>
            <span
              v-if="d.team && d.team.type"
              class="rdm-tag"
            >{{ d.team.type.name }}</span>
            <span
              v-if="d.team && d.team.node && Number(d.team.node_id) !== Number(occurrenceNodeId)"
              class="rdm-tag rdm-tag--node"
            >{{ d.team.node.name }}</span>
          </div>
          <div class="rdm-dispatch__meta">
            <feather-icon
              icon="MapPinIcon"
              size="11"
              class="mr-25"
            />
            <template v-if="d.team && d.team.current_position_type">
              {{ d.team.current_position_type.code }} — {{ d.team.current_position_type.name }}
            </template>
            <template v-else>
              {{ $t('despacho.redirectModal.noPosition') }}
            </template>
          </div>
          <div class="rdm-dispatch__meta">
            {{ $t('despacho.redirectModal.dispatchedAt', { when: formatDateTime(d.dispatched_at || d.created_at) }) }}
            <template v-if="d.from_other_node && d.node">
              · {{ $t('despacho.redirectModal.byNode', { name: d.node.name }) }}
            </template>
          </div>
          <div
            v-if="blockReason(d)"
            class="rdm-dispatch__reason"
          >
            <feather-icon
              icon="SlashIcon"
              size="11"
              class="mr-25"
            />
            {{ blockReason(d) }}
          </div>
        </button>
      </div>
    </template>

    <div class="form-group">
      <label><span class="required">*</span> {{ $t('despacho.detail.redirectTargetLabel') }}</label>
      <v-select
        v-model="targetOccurrenceId"
        :options="pendingOccurrences"
        :reduce="(o) => o.id"
        label="protocol"
        :loading="loadingTargets"
        :placeholder="$t('despacho.detail.redirectTargetPlaceholder')"
        class="vselect"
      >
        <template #option="o">
          <div>{{ o.protocol }}</div>
          <small class="text-muted">{{ o.requester_name }} · {{ o.address }}</small>
        </template>
      </v-select>
      <small
        v-if="!loadingTargets && !pendingOccurrences.length"
        class="text-muted d-block mt-50"
      >
        {{ $t('despacho.detail.redirectNoPendingHint') }}
      </small>
    </div>
    <div class="form-group mb-0">
      <label>{{ $t('despacho.detail.redirectReasonLabel') }}</label>
      <textarea
        v-model="notes"
        class="form-control"
        rows="3"
        :placeholder="$t('despacho.detail.redirectReasonPlaceholder')"
        maxlength="500"
      />
    </div>
  </b-modal>
</template>

<script>
import { BModal, BSpinner } from 'bootstrap-vue'
import vSelect from 'vue-select'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

/**
 * Redireciona a equipe de um despacho ativo para outra ocorrência aguardando
 * despacho.
 *
 *  - Pelo detalhe (`dispatchId` informado): vai direto ao destino.
 *  - Pela fila (sem `dispatchId`): lista os despachos ativos da ocorrência
 *    e pergunta qual — os que não podem ser redirecionados aparecem
 *    desabilitados com o motivo (flag da posição ou nó). O backend revalida.
 */
export default {
  name: 'RedirectDispatchModal',
  components: { BModal, BSpinner, vSelect },
  mixins: [ApiErrorMixin],

  props: {
    visible: { type: Boolean, default: false },
    dispatchId: { type: [Number, String], default: null },
    // Ocorrência de origem: não pode ser destino de si mesma.
    occurrenceId: { type: [Number, String], default: null },
  },

  data() {
    return {
      dispatches: [],
      selectedDispatchId: null,
      occurrenceNodeId: null,
      targetOccurrenceId: null,
      notes: '',
      pendingOccurrences: [],
      loadingDispatches: false,
      loadingTargets: false,
      saving: false,
    }
  },

  methods: {
    reset() {
      this.selectedDispatchId = this.dispatchId || null
      this.dispatches = []
      this.targetOccurrenceId = null
      this.notes = ''
      this.loadPendingOccurrences()
      if (!this.dispatchId) this.loadDispatches()
    },

    async loadDispatches() {
      this.loadingDispatches = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrence_dispatches`, {
          params: { occurrence_id: this.occurrenceId, status: 1 },
        })
        this.dispatches = res.data.data || []
        const first = this.dispatches[0]
        this.occurrenceNodeId = first && first.occurrence ? first.occurrence.node_id : null

        // Um só redirecionável: já vem marcado.
        const eligible = this.dispatches.filter(d => !this.blockReason(d))
        if (eligible.length === 1) this.selectedDispatchId = eligible[0].id
      } catch (e) {
        this.dispatches = []
        this.$message(this.$t('despacho.messages.error'), this.apiErrorText(e), 'error')
      } finally {
        this.loadingDispatches = false
      }
    },

    // Mesma regra do botão: flag da posição atual e permissão do nó.
    blockReason(d) {
      const position = d.team && d.team.current_position_type
      if (!position || !position.allows_redirect) {
        return position
          ? this.$t('despacho.redirectModal.positionBlocks', { code: position.code })
          : this.$t('despacho.redirectModal.noPositionBlocks')
      }
      if (d.can_redirect === false) return this.$t('despacho.redirectModal.nodeBlocks')
      return null
    },

    // Destinos: ocorrências aguardando despacho e ainda sem equipe.
    async loadPendingOccurrences() {
      this.loadingTargets = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrences`, {
          params: {
            with: ['activeResponsibility.user', 'classification', 'dispatchReason', 'discipline'],
            current_step: 'despacha',
            context: 'despacho',
          },
        })
        this.pendingOccurrences = (res.data.data || []).filter(
          o => !o.dispatches_count && Number(o.id) !== Number(this.occurrenceId),
        )
      } catch (e) {
        this.pendingOccurrences = []
      } finally {
        this.loadingTargets = false
      }
    },

    async confirm(bvModalEvent) {
      bvModalEvent.preventDefault()
      if (!this.targetOccurrenceId || !this.selectedDispatchId) return

      this.saving = true
      try {
        await this.$http.post(`${this.$store.state.api}occurrence_dispatches/${this.selectedDispatchId}/redirect`, {
          target_occurrence_id: this.targetOccurrenceId,
          notes: this.notes || null,
        })
        this.$emit('update:visible', false)
        this.$message(this.$t('despacho.messages.success'), this.$t('despacho.messages.redirectSuccess'), 'success')
        this.$emit('redirected')
      } catch (e) {
        this.$message(this.$t('despacho.messages.error'), this.apiErrorText(e, this.$t('despacho.messages.redirectError')), 'error')
      } finally {
        this.saving = false
      }
    },

    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.rdm-section-label { display: block; margin-bottom: 6px; }

.rdm-dispatches {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.rdm-dispatch {
  text-align: left;
  padding: 10px 12px;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.rdm-dispatch:hover:not(:disabled) { border-color: #ff9f43; }
.rdm-dispatch--selected {
  border-color: #ff9f43;
  box-shadow: 0 0 0 2px rgba(255, 159, 67, 0.25);
}
.rdm-dispatch--disabled { cursor: not-allowed; opacity: 0.65; background: #fafafc; }

.rdm-dispatch__head { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; color: #5e5873; }
.rdm-dispatch__check { color: #b9b9c3; }
.rdm-dispatch--selected .rdm-dispatch__check { color: #ff9f43; }
.rdm-dispatch__meta { margin-top: 3px; font-size: 12px; color: #6e6b7b; }
.rdm-dispatch__reason { margin-top: 6px; font-size: 11.5px; font-weight: 600; color: #b45309; }

.rdm-tag {
  padding: 1px 7px;
  border-radius: 9px;
  font-size: 10.5px;
  font-weight: 600;
  background: #f1f2f4;
  color: #4b5563;
}
.rdm-tag--node { background: #fff1e0; color: #9a5b00; }

.dark-layout .rdm-dispatch { background: #283046; border-color: #3b4253; }
.dark-layout .rdm-dispatch--disabled { background: #232a3d; }
.dark-layout .rdm-dispatch__head { color: #d0d2d6; }
.dark-layout .rdm-dispatch__meta { color: #b4b7bd; }
.dark-layout .rdm-dispatch__reason { color: #ffb976; }
.dark-layout .rdm-tag { background: #2c3142; color: #b4b7bd; }
.dark-layout .rdm-tag--node { background: #3a2a14; color: #ffb976; }
</style>
